// ============================================================
// WebSocketClient.ts — Singleton socket.io-client
// Initialiser une seule fois via initSocket() au démarrage de l'app
// ============================================================

import { io, Socket } from 'socket.io-client';

let socket: Socket | null = null;

export interface SocketAuthOptions {
  owner_id: number;
  user_id:  number;
  /** JWT — seul élément d'identité accepté par le serveur au handshake */
  token:    string;
}

export function initSocket(auth: SocketAuthOptions): Socket {
  // Si un socket existe mais n'est plus connecté (ex: token expiré), le détruire avant de recréer
  if (socket) {
    if (socket.connected) return socket;
    socket.disconnect();
    socket = null;
  }

  // Si REACT_APP_API_KEY est un chemin relatif (ex: '/api'), socket.io l'interpréterait
  // comme un namespace et non comme une URL → "Invalid namespace".
  // On utilise window.location.origin pour que le WS se connecte toujours à la même origine.
  const apiKey = process.env.REACT_APP_API_KEY || 'http://localhost:3030';
  const url = apiKey.startsWith('/') ? window.location.origin : apiKey;

  socket = io(url, {
    // owner_id / user_id sont déduits du token côté serveur, jamais transmis
    auth:                  { token: auth.token },
    reconnection:          true,
    reconnectionDelay:     2000,
    reconnectionDelayMax:  10000,
    reconnectionAttempts:  Infinity,
    transports:            ['websocket'],   // pas de polling HTTP long
  });

  socket.on('connect', () => {
    console.log('[WS] Connecté  id:', socket?.id);
    // Signale aux hooks useWsEvent qui auraient mountés avant que le socket
    // ne soit créé qu'ils peuvent maintenant attacher leurs listeners.
    window.dispatchEvent(new CustomEvent('ws-ready'));
  });
  socket.on('disconnect',    (reason) => console.warn('[WS] Déconnecté :', reason));
  socket.on('connect_error', (err)   => console.error('[WS] Erreur connexion :', err.message));

  return socket;
}

/** Retourne le socket existant — throw si initSocket() n'a pas encore été appelé */
export function getSocket(): Socket {
  if (!socket) throw new Error('[WS] Socket non initialisé — appeler initSocket() dans App.tsx');
  return socket;
}

export function disconnectSocket(): void {
  socket?.disconnect();
  socket = null;
}

/** Rejoindre la room d'un département (après sélection du département actif) */
export function joinDept(dept_id: number): void {
  socket?.emit('join:dept', dept_id);
}

/** Quitter la room d'un département */
export function leaveDept(dept_id: number): void {
  socket?.emit('leave:dept', dept_id);
}
